import type { FlowNode } from '@fd/finance-core';
import { ArrowLeft, ArrowUpRight, ChevronRight, LayoutGrid } from 'lucide-react';
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../app/i18n.tsx';
import { CategoryIcon, Segmented } from './primitives.tsx';

// The flow taken apart, in the manner of Open Books' state budget (the owner, 11.10.2026): what
// came in (tiles on the left) runs into one block, "all spending", in the middle, and from it a band
// goes to every category (tiles on the right) with its amount and share. A tile with parts of its
// own (a group) is entered: the block becomes the group, its categories take the right; a
// category opens its entries. Where three columns do not fit, only the block and the categories
// stay, with a switch for the income side; on a phone the block stands above the tiles.

export type DrillNode = Omit<FlowNode, 'children'> & { label: string; /** The most it should come to this month, from the budget. */ limitMinor?: bigint; /** A second, smaller line under the name (whose income it is). */ sub?: string | null; /** What the tile means, on hover. */ hint?: string; color: string; icon?: string | null; children?: DrillNode[] };
type Side = 'spending' | 'income';
/** The month's budget as a whole: what is spent of what may be, and how far the month is. */
export type DrillBudget = { limitMinor: bigint; spentMinor: bigint; expectedMinor: bigint; daysLeft: number; current: boolean };
type Spot = { top: number; height: number };
/** Three columns when wide, block and tiles when narrower, one over the other on a phone. */
export type DrillMode = 'three' | 'two' | 'stack';
/** One band: its end at the block (y, height) and at its tile. */
type Band = { node: DrillNode; yb: number; hb: number; yt: number; ht: number; lanes: Lanes };

/** The block's share of the column's height: a little air above and below, as in the example. */
const BLOCK = 0.86;
/** How far a band runs on under its tile, past the tile's rounded corners, so no gap shows there. */
const UNDER = 16;
/** How far the bands reach back into the block: they fade in there, out of its blended edge. */
const LEAD = 72;
/** Room kept above the block for its caption (all income, its sum), and below it for the spending's. */
const CAPTION = 50;
/** The height of that caption: it stands right on the block. */
const MID = 44;
/** The key of the tile on the left that leads back out of a group. */
const BACK = '__back';
/** The same where the block has bands on both sides, and its text needs the room between them. */
const LEAD_BOTH = 34;
/** A band ends this far inside its tile's top and bottom edge, clear of the tile's rounded corners. */
const INSET = 4;
/** Thinnest band, so a small part is still drawn. */
const HAIRLINE = 2;

const slug = (key: string) => key.replace(/[^a-zA-Z0-9_-]/g, '_');

function ribbon(x0: number, y0: number, x1: number, y1: number, h0: number, h1: number): string {
  const mid = (x0 + x1) / 2;
  return `M ${x0},${y0} C ${mid},${y0} ${mid},${y1} ${x1},${y1} L ${x1},${y1 + h1} C ${mid},${y1 + h1} ${mid},${y0 + h0} ${x0},${y0 + h0} Z`;
}

/** Seeded, so a band's lines stay put while the pointer moves over the tiles. */
function seeded(key: string): () => number {
  let h = 1779033703 ^ key.length;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 3432918353);
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let x = Math.imul(a ^ (a >>> 15), 1 | a);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/** The lines inside one band, as on Open Books: fine still lines, and dashes running along the band. */
type Lanes = { fine: { t: number; op: number }[]; flow: { t: number; wide: number; core: number; opWide: number; opCore: number; dash: number; gap: number; dur: number; delay: number }[] };
function lanesOf(key: string, height: number): Lanes {
  const rand = seeded(key);
  const fineCount = Math.max(1, Math.round(height / 5));
  const fine = Array.from({ length: fineCount }, (_, i) => ({ t: fineCount === 1 ? 0.5 : (i + 0.5) / fineCount, op: 0.06 + rand() * 0.15 }));
  const flowCount = Math.max(1, Math.round(height / 7.5));
  const flow = Array.from({ length: flowCount }, (_, i) => {
    const wide = 3.2 + rand() * 6.4;
    const dash = 7.5 + rand() * 12.5;
    const gap = 20 + rand() * 33;
    const speed = 15 + rand() * 6;
    const dur = (dash + gap) / speed;
    const opWide = 0.11 + rand() * 0.07;
    return { t: flowCount === 1 ? 0.5 : 0.06 + ((i + rand() * 0.8) / flowCount) * 0.88, wide, core: wide * 0.312, opWide, opCore: Math.min(0.6, opWide * 3.3), dash, gap, dur, delay: -rand() * dur };
  });
  return { fine, flow };
}

export function FlowDrill({
  sources,
  targets,
  incomeMinor,
  budget = null,
  mode,
  privacy,
  amount,
  opens,
  onOpen,
}: {
  sources: readonly DrillNode[];
  targets: readonly DrillNode[];
  incomeMinor: bigint;
  budget?: DrillBudget | null;
  mode: DrillMode;
  privacy: boolean;
  amount(value: bigint): string;
  opens(node: FlowNode): boolean;
  onOpen?(node: FlowNode): void;
}) {
  const t = useT();
  const id = useId().replace(/:/g, '');
  const three = mode === 'three';
  const stack = mode === 'stack';
  const lead = three ? LEAD_BOTH : LEAD;
  const [sideChosen, setSide] = useState<Side>('spending');
  const side: Side = three ? 'spending' : sideChosen;
  const [focusKey, setFocus] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);

  const bySize = (list: readonly DrillNode[]) => [...list].sort((a, b) => (b.valueMinor > a.valueMinor ? 1 : b.valueMinor < a.valueMinor ? -1 : 0));
  const spendTop = useMemo(() => targets.filter((n) => (n.kind === 'expense' || n.kind === 'otherExpense') && n.valueMinor > 0n), [targets]);
  // Only what came in: money taken from balances belongs to the full picture with savings, which this view leaves out.
  const incomeTop = useMemo(() => sources.filter((n) => n.kind === 'income' && n.valueMinor > 0n), [sources]);
  const top = side === 'spending' ? spendTop : incomeTop;
  // A group left behind by a new period or the other side: back to the whole.
  const focus = focusKey ? (top.find((n) => n.key === focusKey && (n.children?.length ?? 0) > 0) ?? null) : null;
  const parts = useMemo(() => bySize(focus ? focus.children! : top), [focus, top]);
  // Inside a group the left shows where it was entered from: one tile that leads back.
  const wholeTotal = top.reduce((sum, n) => sum + n.valueMinor, 0n);
  const wholeLabel = t.t(side === 'spending' ? 'dashboard.flowAllSpending' : 'dashboard.flowAllIncome');
  const back = useMemo<DrillNode | null>(
    () => (focus ? ({ key: BACK, kind: 'expense', categoryId: null, valueMinor: wholeTotal, label: wholeLabel, color: focus.color, icon: null } as DrillNode) : null),
    [focus, wholeTotal, wholeLabel],
  );
  const left = useMemo(() => (three ? (back ? [back] : bySize(incomeTop)) : []), [three, back, incomeTop]);
  const total = focus ? focus.valueMinor : top.reduce((sum, n) => sum + n.valueMinor, 0n);
  const leftTotal = left.reduce((sum, n) => sum + n.valueMinor, 0n);
  const incomeTotal = incomeTop.reduce((sum, n) => sum + n.valueMinor, 0n);
  const shareOf = (value: bigint, of: bigint) => (of > 0n ? Number((value * 10000n) / of) / 10000 : 0);
  const ofIncome = side === 'spending' && incomeMinor > 0n && !privacy ? t.t('dashboard.flowShareOfIncome', { share: t.percent(Number((total * 10000n) / incomeMinor) / 10000) }) : null;

  // Where each tile stands, so every band ends on its own tile.
  const listRight = useRef<HTMLOListElement | null>(null);
  const listLeft = useRef<HTMLOListElement | null>(null);
  const bandsRight = useRef<HTMLDivElement | null>(null);
  const bandsLeft = useRef<HTMLDivElement | null>(null);
  const [spots, setSpots] = useState<{ height: number; right: number; left: number; tilesRight: Spot[]; tilesLeft: Spot[] }>({ height: 0, right: 0, left: 0, tilesRight: [], tilesLeft: [] });
  useLayoutEffect(() => {
    const frame = bandsRight.current;
    const right = listRight.current;
    if (!right) return;
    const measure = () => {
      // The tile itself, not its row: a band is never taller than the tile it belongs to. Measured
      // against the bands' own box, so it holds wherever the card sits and whatever scrolls.
      const origin = (frame ?? right).getBoundingClientRect().top;
      const tilesOf = (list: HTMLOListElement | null) =>
        list
          ? [...list.children].map((li) => {
              const box = (li.firstElementChild as HTMLElement).getBoundingClientRect();
              return { top: box.top - origin, height: box.height };
            })
          : [];
      setSpots({ height: frame?.offsetHeight ?? right.offsetHeight, right: frame?.offsetWidth ?? 0, left: bandsLeft.current?.offsetWidth ?? 0, tilesRight: tilesOf(right), tilesLeft: tilesOf(listLeft.current) });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(right);
    if (listLeft.current) observer.observe(listLeft.current);
    if (frame) observer.observe(frame);
    if (bandsLeft.current) observer.observe(bandsLeft.current);
    return () => observer.disconnect();
  }, [parts, left, mode]);

  // As on Open Books: the bands together fill the block; each narrows to the height of its tile
  // (never taller than it) where it arrives.
  // The block stands level with the tiles on the right: from the top of the first to the bottom of
  // the last (kept clear of the captions above it, and tall enough for its text).
  const lastRight = spots.tilesRight.at(-1);
  const span = lastRight && spots.tilesRight.length === parts.length ? { top: spots.tilesRight[0]!.top, bottom: lastRight.top + lastRight.height } : null;
  const floor = three ? CAPTION : 0;
  const fallback = Math.max(Math.min(spots.height * BLOCK, spots.height - floor - 8), Math.min(spots.height - floor, 110));
  const blockTop = span ? Math.max(span.top, floor) : Math.max((spots.height - fallback) / 2, floor);
  const blockHeight = span ? Math.min(Math.max(span.bottom - blockTop, Math.min(110, spots.height - blockTop)), spots.height - blockTop) : fallback;
  const build = (list: readonly DrillNode[], tiles: Spot[], of: bigint, tag: string): Band[] => {
    // The bands start under the block, inside its top and bottom edge, and run on under the tiles.
    let along = blockTop + INSET;
    return list.map((node, i) => {
      const tile = tiles[i];
      const hb = Math.max(HAIRLINE, shareOf(node.valueMinor, of) * (blockHeight - 2 * INSET));
      const ht = tile ? Math.max(HAIRLINE, Math.min(hb, tile.height - 2 * INSET)) : hb;
      const yt = tile ? tile.top + (tile.height - ht) / 2 : along;
      const band = { node, yb: along, hb, yt, ht, lanes: lanesOf(`${tag}${node.key}`, hb) };
      along += hb;
      return band;
    });
  };
  const bandsOut = useMemo(() => build(parts, spots.tilesRight, total, 'r'), [parts, spots, blockTop, blockHeight, total]);
  const bandsIn = useMemo(() => build(left, spots.tilesLeft, leftTotal, 'l'), [left, spots, blockTop, blockHeight, leftTotal]);

  // The block's edges take on the colours of the bands at them, blended; the bands themselves fade
  // in over that blend (LEAD), so the block runs into them without a seam.
  const washOf = (bands: Band[]) => {
    if (bands.length === 0 || blockHeight <= 0) return undefined;
    const stops = bands.map(({ node, yb, hb }) => `${node.color} ${((yb + hb / 2 - blockTop) / blockHeight) * 100}%`);
    return `linear-gradient(180deg, ${stops.join(', ')})`;
  };

  const enter = (node: DrillNode, entering: boolean) => {
    if (node.key === BACK) {
      setFocus(null);
      setHover(null);
    } else if (entering && (node.children?.length ?? 0) > 0 && !focus) {
      setFocus(node.key);
      setHover(null);
    } else if (opens(node)) onOpen?.(node);
  };
  const action = (node: DrillNode, entering: boolean) => (node.key === BACK ? 'back' : entering && (node.children?.length ?? 0) > 0 && !focus ? 'enter' : opens(node) && onOpen ? 'open' : null);
  const whole = wholeLabel;
  // The budget under the diagram: the whole month's, or the group's own limit once it is entered.
  const meter: DrillBudget | null = focus
    ? focus.limitMinor
      ? { limitMinor: focus.limitMinor, spentMinor: focus.valueMinor, expectedMinor: 0n, daysLeft: 0, current: false }
      : null
    : budget && budget.limitMinor > 0n
      ? budget
      : null;
  const pick = (s: Side) => {
    setSide(s);
    setFocus(null);
  };

  if (top.length === 0 && left.length === 0) {
    return (
      <div className="drill">
        {!three && <DrillBar side={side} setSide={pick} whole={whole} focus={null} back={() => setFocus(null)} switchable />}
        <p className="text-secondary drill__empty">{t.t('dashboard.flowEmpty')}</p>
      </div>
    );
  }

  const tiles = (list: readonly DrillNode[], of: bigint, dir: 'l' | 'r', ref: React.RefObject<HTMLOListElement | null>, entering: boolean) => (
    <ol ref={ref} className="drill__tiles" style={{ '--n': list.length } as React.CSSProperties}>
      {list.map((node, i) => {
        const does = action(node, entering);
        const part = shareOf(node.valueMinor, of);
        const key = dir === 'l' ? `l:${node.key}` : node.key;
        // A category with a budget reads "spent / may be spent" and how much of it is used; any other,
        // what it is of the whole.
        const limit = dir === 'r' ? node.limitMinor : undefined;
        const used = limit ? Number((node.valueMinor * 10000n) / limit) / 10000 : part;
        return (
          <li key={node.key} style={{ '--i': i } as React.CSSProperties} title={node.hint}>
            <button
              type="button"
              className={`drill__tile${hover === key ? ' is-lit' : ''}`}
              style={{ '--c': node.color } as React.CSSProperties}
              disabled={does === null}
              onClick={() => enter(node, entering)}
              onMouseEnter={() => setHover(key)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(key)}
              onBlur={() => setHover(null)}
              title={does === 'enter' ? t.t('dashboard.flowEnter') : does === 'open' ? t.t('dashboard.flowOpenEntries') : does === 'back' ? t.t('dashboard.flowBackHint') : undefined}
            >
              <span className="drill__tile-head">
                {node.key === BACK ? (
                  <span className="cat-icon" style={{ background: node.color, color: '#fff' }} aria-hidden="true">
                    <LayoutGrid />
                  </span>
                ) : (
                  <CategoryIcon icon={node.icon ?? null} color={node.color} />
                )}
                <span className="drill__tile-name">
                  <span className="drill__tile-title">{node.label}</span>
                  {node.sub && <span className="drill__tile-sub">{node.sub}</span>}
                </span>
                <span className="drill__tile-amount">
                  <span className="money">{limit ? `${amount(node.valueMinor)} / ${amount(limit)}` : amount(node.valueMinor)}</span>
                  {!privacy && node.key !== BACK && <span className={`drill__tile-pct${limit && used > 1 ? ' is-over' : ''}`}>{t.percent(used)}</span>}
                </span>
                {does && <span className="drill__tile-go" aria-hidden="true">{does === 'enter' ? <ChevronRight /> : does === 'back' ? <ArrowLeft /> : <ArrowUpRight />}</span>}
              </span>
              <span className="drill__tile-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, Math.max(1, used * 100))}%`, background: limit && used > 1 ? 'var(--fill-expense, #e5484d)' : node.color }} />
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className={`drill drill--${mode}`} style={{ '--lead': `${lead}px` } as React.CSSProperties}>
      {/* Three columns need no bar: the middle says what it is, and the tile on the left leads back. */}
      {!three && <DrillBar side={side} setSide={pick} whole={whole} focus={focus} back={() => setFocus(null)} switchable />}
      <div className="drill__stage" key={`${side}:${focus?.key ?? ''}`}>
        {three && (
          <div className="drill__col drill__col--left">
            <span className="drill__cap">
              {back ? t.t('dashboard.flowBackCaption') : t.t('dashboard.flowWhereFrom')}
            </span>
            {tiles(left, leftTotal, 'l', listLeft, false)}
          </div>
        )}
        {three && (
          <div ref={bandsLeft} className="drill__bands drill__bands--left" aria-hidden="true">
            <Streams id={`${id}l`} dir="in" bands={bandsIn} width={spots.left} height={spots.height} hover={hover} prefix="l:" lead={lead} />
          </div>
        )}
        {three && (
          <div className="drill__mid" style={{ marginTop: Math.max(0, blockTop - MID) }}>
            <span className="drill__overline">
              {focus && <CategoryIcon icon={focus.icon ?? null} color={focus.color} />}
              {focus ? focus.label : t.t('dashboard.flowAllIncome')}
            </span>
            <span className="drill__sum">
              <span className="drill__total money">{amount(focus ? total : incomeTotal)}</span>
              {focus && ofIncome && <span className="drill__note">{ofIncome}</span>}
            </span>
          </div>
        )}
        {three && !focus && (
          <div className="drill__spend drill__spend--under" style={{ marginTop: Math.min(0, blockTop + blockHeight - spots.height) + 8 }}>
            <span className="drill__overline">{t.t('dashboard.flowAllSpending')}</span>
            <span className="drill__sum">
              <span className="drill__total money">{amount(total)}</span>
              {ofIncome && <span className="drill__note">{ofIncome}</span>}
            </span>
          </div>
        )}
        <div className="drill__block" style={stack ? undefined : { marginTop: blockTop, height: blockHeight || undefined }}>
          {three && <span className="drill__wash drill__wash--left" style={{ background: washOf(bandsIn) }} aria-hidden="true" />}
          {!stack && <span className="drill__wash drill__wash--soft" style={{ background: washOf(bandsOut) }} aria-hidden="true" />}
          <span className="drill__overline">
            {focus && <CategoryIcon icon={focus.icon ?? null} color={focus.color} />}
            {focus ? t.t(side === 'spending' ? 'dashboard.flowGroupLabel' : 'dashboard.income') : whole}
          </span>
          {focus && <strong className="drill__name">{focus.label}</strong>}
          <span className="drill__total money">{amount(total)}</span>
          {ofIncome && <span className="drill__note">{ofIncome}</span>}
        </div>
        {!stack && (
          <div ref={bandsRight} className="drill__bands drill__bands--right" aria-hidden="true">
            <Streams id={`${id}r`} dir="out" bands={bandsOut} width={spots.right} height={spots.height} hover={hover} prefix="" lead={lead} />
          </div>
        )}
        <div className="drill__col drill__col--right">
          {three && <span className="drill__cap">{t.t('dashboard.flowCaptionCategories')}</span>}
          {tiles(parts, total, 'r', listRight, true)}
        </div>
      </div>
      {meter && (
        <div className="drill__foot">
          <BudgetMeter meter={meter} amount={amount} />
        </div>
      )}
      <p className="drill__hint">{t.t(focus ? 'dashboard.flowDrillHintIn' : 'dashboard.flowDrillHint')}</p>
    </div>
  );
}

/** The bands between a column of tiles and the block: "out" runs from the block to its tiles, "in" from tiles to the block. */
function Streams({ id, dir, bands, width, height, hover, prefix, lead }: { id: string; dir: 'in' | 'out'; bands: Band[]; width: number; height: number; hover: string | null; prefix: string; lead: number }) {
  /** The bands' whole length: back into the block (lead), across the gap, on under the tiles (UNDER). */
  const reach = lead + width + UNDER;
  const mid = dir === 'out' ? (lead + reach) / 2 : (reach - lead) / 2;
  const shape = ({ yb, hb, yt, ht }: Band) =>
    dir === 'out'
      ? `M 0,${yb} H ${lead} V ${yb + hb} H 0 Z ${ribbon(lead, yb, reach, yt, hb, ht)}`
      : `${ribbon(0, yt, reach - lead, yb, ht, hb)} M ${reach - lead},${yb} H ${reach} V ${yb + hb} H ${reach - lead} Z`;
  const lane = ({ yb, hb, yt, ht }: Band, tt: number) => {
    const ya = yb + tt * hb;
    const yc = yt + tt * ht;
    return dir === 'out' ? `M 0,${ya} L ${lead},${ya} C ${mid},${ya} ${mid},${yc} ${reach},${yc}` : `M 0,${yc} C ${mid},${yc} ${mid},${ya} ${reach - lead},${ya} L ${reach},${ya}`;
  };
  return (
    <svg className={`drill__svg drill__svg--${dir}`} style={{ marginLeft: dir === 'out' ? -lead : -UNDER }} width={reach} height={height} viewBox={`0 0 ${reach || 1} ${height || 1}`}>
      <defs>
        {bands.map(({ node }) => (
          <linearGradient key={node.key} id={`wsteg-${id}-${slug(node.key)}`} gradientUnits="userSpaceOnUse" x1={0} x2={reach}>
            <stop offset="0%" stopColor={node.color} />
            <stop offset="45%" stopColor={`color-mix(in srgb, ${node.color} 84%, #000)`} />
            <stop offset="100%" stopColor={node.color} />
          </linearGradient>
        ))}
      </defs>
      <g className="drill__ribbons">
        {bands.map((band) => {
          const { node, hb, lanes } = band;
          const opacity = hover === null || hover === `${prefix}${node.key}` ? 1 : 0.3;
          const pale = (mix: number) => `color-mix(in srgb, ${node.color} ${mix}%, #fff)`;
          return (
            <g key={node.key} style={{ opacity, transition: 'opacity 140ms' }}>
              <path d={shape(band)} fill={`url(#wsteg-${id}-${slug(node.key)})`} opacity={0.9} />
              <path d={lane(band, 0.06)} fill="none" stroke="#fff" strokeWidth={hb >= 10 ? 2.5 : 1} opacity={0.3} />
              {lanes.fine.map((l, i) => (
                <path key={i} d={lane(band, l.t)} fill="none" stroke="#fff" strokeWidth={1} opacity={l.op} />
              ))}
              {lanes.flow.map((l, i) => {
                const run = { strokeDasharray: `${l.dash} ${l.gap}`, strokeLinecap: 'round' as const, ['--ob-okres' as string]: `${l.dash + l.gap}px`, animationDuration: `${l.dur}s`, animationDelay: `${l.delay}s` };
                return (
                  <g key={i}>
                    <path className="drill__flow" d={lane(band, l.t)} fill="none" style={{ ...run, stroke: pale(14) }} strokeWidth={l.wide} opacity={l.opWide} />
                    <path className="drill__flow" d={lane(band, l.t)} fill="none" style={{ ...run, stroke: pale(24) }} strokeWidth={l.core} opacity={l.opCore} />
                  </g>
                );
              })}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function DrillBar({ side, setSide, whole, focus, back, switchable }: { side: Side; setSide(side: Side): void; whole: string; focus: DrillNode | null; back(): void; switchable: boolean }) {
  const t = useT();
  return (
    <div className="drill__bar">
      {switchable && (
        <Segmented<Side>
          label={t.t('dashboard.flowSide')}
          value={side}
          onChange={setSide}
          options={[
            { value: 'spending', label: t.t('dashboard.flowWhereTo') },
            { value: 'income', label: t.t('dashboard.flowWhereFrom') },
          ]}
        />
      )}
      <nav className="drill__crumbs" aria-label={t.t('dashboard.flowCrumbs')}>
        <button type="button" className={`drill__chip${focus ? '' : ' is-on'}`} onClick={back} aria-current={focus ? undefined : 'true'}>
          {whole}
        </button>
        {focus && (
          <>
            <ChevronRight className="drill__sep" aria-hidden="true" />
            <span className="drill__chip is-on" aria-current="true">
              <CategoryIcon icon={focus.icon ?? null} color={focus.color} />
              {focus.label}
            </span>
          </>
        )}
      </nav>
    </div>
  );
}

/** The budget as one bar: what is spent of what may be, the steady pace as a tick, what is left. */
function BudgetMeter({ meter, amount }: { meter: DrillBudget; amount(value: bigint): string }) {
  const t = useT();
  const used = Number(meter.spentMinor) / Number(meter.limitMinor);
  const left = meter.limitMinor - meter.spentMinor;
  const text = t.t('overview.spentOf', { spent: amount(meter.spentMinor), limit: amount(meter.limitMinor) });
  // Where the steady pace stands by today, in percent of the bar (only in the month it is running).
  const pace = meter.current && meter.expectedMinor > 0n ? Math.min(100, (Number(meter.expectedMinor) / Number(meter.limitMinor)) * 100) : null;
  return (
    <div className="drill__meter">
      <span className="drill__overline">{t.t('overview.budget')}</span>
      <span className="drill__meter-text">{text}</span>
      <div className="drill__meter-bar" role="img" aria-label={text}>
        <span className={`drill__meter-fill${used > 1 ? ' is-over' : ''}`} style={{ width: `${Math.min(100, used * 100)}%` }} />
        {pace !== null && (
          <>
            <span className="drill__meter-pace" style={{ left: `${pace}%` }} title={t.t('overview.budgetPace')} />
            {/* The tick says what it is, inside the bar beside it: on the side where there is room. */}
            <span className={`drill__meter-flag${pace > 72 ? ' is-before' : ''}`} style={{ left: `${pace}%` }} title={t.t('overview.budgetPace')}>
              {t.t('dashboard.flowPaceShort', { amount: amount(meter.expectedMinor) })}
            </span>
          </>
        )}
      </div>
      <span className="drill__meter-left">
        <span className={left < 0n ? 'fg-expense' : ''}>{left >= 0n ? t.t('overview.remaining', { amount: amount(left) }) : t.t('overview.overBy', { amount: amount(-left) })}</span>
        {meter.current && meter.daysLeft > 0 && <span className="text-muted"> · {t.plural('overview.daysLeft', meter.daysLeft)}</span>}
      </span>
    </div>
  );
}
